// src/store/slices/chatSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface Message {
  id: string;
  threadId: string;
  senderId: string;
  senderName: string;
  senderRole: 'client' | 'stylist' | 'admin';
  senderPhoto?: string;
  content: string;
  read: boolean;
  createdAt: string;
}

export interface MessageThread {
  id: string;
  appointmentId: string;
  clientId: string;
  clientName: string;
  clientPhoto?: string;
  stylistId: string;
  stylistName: string;
  stylistPhoto?: string;
  lastMessage?: Message;
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

interface ChatState {
  threads: MessageThread[];
  currentThread: MessageThread | null;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatState = {
  threads: [],
  currentThread: null,
  messages: [],
  isLoading: false,
  error: null,
};

// Mock data for development
const mockThreads: MessageThread[] = [
  {
    id: 'thread1',
    appointmentId: '1',
    clientId: 'client1',
    clientName: 'John Doe',
    clientPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
    stylistId: 'stylist1',
    stylistName: 'Emma Thompson',
    stylistPhoto: 'https://randomuser.me/api/portraits/women/44.jpg',
    unreadCount: 1,
    createdAt: '2025-04-02T10:00:00Z',
    updatedAt: '2025-04-03T14:30:00Z'
  },
  {
    id: 'thread2',
    appointmentId: '3',
    clientId: 'client1',
    clientName: 'John Doe',
    clientPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
    stylistId: 'stylist3',
    stylistName: 'Lisa Wong',
    stylistPhoto: 'https://randomuser.me/api/portraits/women/63.jpg',
    unreadCount: 0,
    createdAt: '2025-03-10T09:15:00Z',
    updatedAt: '2025-03-15T11:45:00Z'
  }
];

const mockMessages: Record<string, Message[]> = {
  'thread1': [
    {
      id: 'msg1',
      threadId: 'thread1',
      senderId: 'client1',
      senderName: 'John Doe',
      senderRole: 'client',
      senderPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
      content: 'Hi Emma, I was wondering if I should use any specific products before my appointment?',
      read: true,
      createdAt: '2025-04-02T10:00:00Z'
    },
    {
      id: 'msg2',
      threadId: 'thread1',
      senderId: 'stylist1',
      senderName: 'Emma Thompson',
      senderRole: 'stylist',
      senderPhoto: 'https://randomuser.me/api/portraits/women/44.jpg',
      content: 'Hi John! For your haircut, just make sure your hair is clean but don\'t use heavy conditioners or styling products on the day of your appointment.',
      read: true,
      createdAt: '2025-04-02T10:15:00Z'
    },
    {
      id: 'msg3',
      threadId: 'thread1',
      senderId: 'client1',
      senderName: 'John Doe',
      senderRole: 'client',
      senderPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
      content: 'Great, thanks for the advice!',
      read: true,
      createdAt: '2025-04-02T10:20:00Z'
    },
    {
      id: 'msg4',
      threadId: 'thread1',
      senderId: 'stylist1',
      senderName: 'Emma Thompson',
      senderRole: 'stylist',
      senderPhoto: 'https://randomuser.me/api/portraits/women/44.jpg',
      content: 'Looking forward to seeing you tomorrow. Feel free to bring reference photos if you have a specific style in mind!',
      read: false,
      createdAt: '2025-04-03T14:30:00Z'
    }
  ],
  'thread2': [
    {
      id: 'msg5',
      threadId: 'thread2',
      senderId: 'client1',
      senderName: 'John Doe',
      senderRole: 'client',
      senderPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
      content: 'Hi Lisa, I\'m excited about my hair coloring appointment!',
      read: true,
      createdAt: '2025-03-10T09:15:00Z'
    },
    {
      id: 'msg6',
      threadId: 'thread2',
      senderId: 'stylist3',
      senderName: 'Lisa Wong',
      senderRole: 'stylist',
      senderPhoto: 'https://randomuser.me/api/portraits/women/63.jpg',
      content: 'Hi John! I\'m excited too. Have you decided on a color?',
      read: true,
      createdAt: '2025-03-10T09:30:00Z'
    },
    {
      id: 'msg7',
      threadId: 'thread2',
      senderId: 'client1',
      senderName: 'John Doe',
      senderRole: 'client',
      senderPhoto: 'https://randomuser.me/api/portraits/men/75.jpg',
      content: 'I\'m thinking of a dark brown with subtle highlights. What do you think?',
      read: true,
      createdAt: '2025-03-10T09:45:00Z'
    },
    {
      id: 'msg8',
      threadId: 'thread2',
      senderId: 'stylist3',
      senderName: 'Lisa Wong',
      senderRole: 'stylist',
      senderPhoto: 'https://randomuser.me/api/portraits/women/63.jpg',
      content: 'That would look great with your skin tone! I can\'t wait to see the result.',
      read: true,
      createdAt: '2025-03-10T10:00:00Z'
    }
  ]
};

// Update the last message in each thread
mockThreads.forEach(thread => {
  const threadMessages = mockMessages[thread.id];
  if (threadMessages && threadMessages.length > 0) {
    thread.lastMessage = threadMessages[threadMessages.length - 1];
  }
});

// Async thunks
export const fetchMessageThreads = createAsyncThunk(
  'chat/fetchThreads',
  async (_, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.get('/messages/threads');
      // return response.data;
      
      // Using mock data for development
      return mockThreads;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch message threads');
    }
  }
);

export const fetchThreadMessages = createAsyncThunk(
  'chat/fetchMessages',
  async (threadId: string, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.get(`/messages/threads/${threadId}`);
      // return { threadId, messages: response.data };
      
      // Using mock data for development
      return { 
        threadId, 
        messages: mockMessages[threadId] || [] 
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch messages');
    }
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({
    threadId,
    content,
    senderId,
    senderName,
    senderRole,
    senderPhoto
  }: {
    threadId: string | null;
    content: string;
    senderId: string;
    senderName: string;
    senderRole: 'client' | 'stylist' | 'admin';
    senderPhoto?: string;
  }, { rejectWithValue }) => {
    try {
      // Check if threadId is null
      if (!threadId) {
        throw new Error('Thread ID is required to send a message');
      }
      
      // In a real app, this would be an API call
      // const response = await apiClient.post(`/messages/threads/${threadId}`, { content });
      // return response.data;
      
      // Using mock data for development
      const newMessage: Message = {
        id: `msg${Date.now()}`,
        threadId,
        senderId,
        senderName,
        senderRole,
        senderPhoto,
        content,
        read: false,
        createdAt: new Date().toISOString()
      };
      
      // Update the mock data
      if (!mockMessages[threadId]) {
        mockMessages[threadId] = [];
      }
      mockMessages[threadId].push(newMessage);
      
      // Update the thread
      const threadIndex = mockThreads.findIndex(t => t.id === threadId);
      if (threadIndex !== -1) {
        mockThreads[threadIndex].lastMessage = newMessage;
        mockThreads[threadIndex].updatedAt = newMessage.createdAt;
        if (senderId !== mockThreads[threadIndex].clientId) {
          mockThreads[threadIndex].unreadCount += 1;
        }
      }
      
      return newMessage;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send message');
    }
  }
);

export const markThreadAsRead = createAsyncThunk(
  'chat/markThreadAsRead',
  async (threadId: string, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // await apiClient.patch(`/messages/threads/${threadId}/read`);
      
      // Using mock data for development
      const threadIndex = mockThreads.findIndex(t => t.id === threadId);
      if (threadIndex === -1) {
        throw new Error('Thread not found');
      }
      
      mockThreads[threadIndex].unreadCount = 0;
      
      // Mark all messages as read
      if (mockMessages[threadId]) {
        mockMessages[threadId].forEach(msg => {
          msg.read = true;
        });
      }
      
      return threadId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark thread as read');
    }
  }
);

export const createMessageThread = createAsyncThunk(
  'chat/createThread',
  async ({
    appointmentId,
    clientId,
    clientName,
    clientPhoto,
    stylistId,
    stylistName,
    stylistPhoto,
    initialMessage
  }: {
    appointmentId: string;
    clientId: string;
    clientName: string;
    clientPhoto?: string;
    stylistId: string;
    stylistName: string;
    stylistPhoto?: string;
    initialMessage: string;
  }, { rejectWithValue }) => {
    try {
      // In a real app, this would be an API call
      // const response = await apiClient.post('/messages/threads', {
      //   appointmentId,
      //   initialMessage
      // });
      // return response.data;
      
      // Using mock data for development
      const now = new Date().toISOString();
      
      // Create a new thread
      const newThread: MessageThread = {
        id: `thread${Date.now()}`,
        appointmentId,
        clientId,
        clientName,
        clientPhoto,
        stylistId,
        stylistName,
        stylistPhoto,
        unreadCount: 0,
        createdAt: now,
        updatedAt: now
      };
      
      // Create initial message
      const newMessage: Message = {
        id: `msg${Date.now()}`,
        threadId: newThread.id,
        senderId: clientId,
        senderName: clientName,
        senderRole: 'client',
        senderPhoto: clientPhoto,
        content: initialMessage,
        read: false,
        createdAt: now
      };
      
      // Update mock data
      mockThreads.push(newThread);
      mockMessages[newThread.id] = [newMessage];
      newThread.lastMessage = newMessage;
      
      return { thread: newThread, message: newMessage };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create message thread');
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setCurrentThread: (state, action: PayloadAction<string>) => {
      state.currentThread = state.threads.find(thread => thread.id === action.payload) || null;
    },
    clearCurrentThread: (state) => {
      state.currentThread = null;
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchMessageThreads
      .addCase(fetchMessageThreads.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessageThreads.fulfilled, (state, action: PayloadAction<MessageThread[]>) => {
        state.isLoading = false;
        state.threads = action.payload;
      })
      .addCase(fetchMessageThreads.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // fetchThreadMessages
      .addCase(fetchThreadMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchThreadMessages.fulfilled, (state, action: PayloadAction<{ threadId: string; messages: Message[] }>) => {
        state.isLoading = false;
        state.messages = action.payload.messages;
        
        // Set the current thread if it matches
        if (state.currentThread?.id !== action.payload.threadId) {
          state.currentThread = state.threads.find(thread => thread.id === action.payload.threadId) || null;
        }
      })
      .addCase(fetchThreadMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // sendMessage
      .addCase(sendMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action: PayloadAction<Message>) => {
        state.isLoading = false;
        state.messages.push(action.payload);
        
        // Update the thread's last message
        const threadIndex = state.threads.findIndex(thread => thread.id === action.payload.threadId);
        if (threadIndex !== -1) {
          state.threads[threadIndex].lastMessage = action.payload;
          state.threads[threadIndex].updatedAt = action.payload.createdAt;
          
          // If this is the current thread, also update it
          if (state.currentThread?.id === action.payload.threadId) {
            state.currentThread.lastMessage = action.payload;
            state.currentThread.updatedAt = action.payload.createdAt;
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // markThreadAsRead
      .addCase(markThreadAsRead.fulfilled, (state, action: PayloadAction<string>) => {
        // Update thread unread count
        const threadIndex = state.threads.findIndex(thread => thread.id === action.payload);
        if (threadIndex !== -1) {
          state.threads[threadIndex].unreadCount = 0;
          
          // If this is the current thread, also update it
          if (state.currentThread?.id === action.payload) {
            state.currentThread.unreadCount = 0;
          }
        }
        
        // Mark all messages as read
        state.messages.forEach(message => {
          if (message.threadId === action.payload) {
            message.read = true;
          }
        });
      })
      
      // createMessageThread
      .addCase(createMessageThread.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createMessageThread.fulfilled, (state, action: PayloadAction<{ thread: MessageThread; message: Message }>) => {
        state.isLoading = false;
        state.threads.unshift(action.payload.thread);
        state.currentThread = action.payload.thread;
        state.messages = [action.payload.message];
      })
      .addCase(createMessageThread.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentThread, clearCurrentThread } = chatSlice.actions;

export default chatSlice.reducer;